import { AsyncPipe, NgIf } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, ViewChild } from '@angular/core';
import {
    MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
    MatLegacyDialogModule as MatDialogModule,
    MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { loadConnectAndInitialize, StripeConnectInstance } from '@stripe/connect-js';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, defer, of } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { StripeConnectService } from '../../../../services/stripe-treasury/stripe-connect.service';
import { GrowlerService } from '../../../shared/growler/growler.service';
import { PAYHOA_URLS, STRIPE_APPEARANCE_CONFIG } from '../../../v2-layout/stripe-treasury/stripe-treasury.constants';

export interface BankAccountStripeOnboardingDialogInput {
    organizationId: number;
}

export interface BankAccountStripeOnboardingDialogResult {
    exited: boolean;
    error?: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-bank-account-stripe-onboarding-dialog',
    templateUrl: './bank-account-stripe-onboarding-dialog.component.html',
    styleUrls: ['./bank-account-stripe-onboarding-dialog.component.scss'],
    standalone: true,
    imports: [
        MatDialogModule,
        MatIconModule,
        MatProgressSpinnerModule,
        NgIf,
        AsyncPipe,
    ],
})
export class BankAccountStripeOnboardingDialogComponent implements AfterViewInit, OnDestroy {
    static readonly width = MatDialogSizes.MD;

    @ViewChild('stripeContainer') stripeContainer: ElementRef<HTMLDivElement>;

    readonly loading$ = new BehaviorSubject<boolean>(true);

    private _stripeConnectInstance: StripeConnectInstance | null = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: BankAccountStripeOnboardingDialogInput,
        private _dialogRef: MatDialogRef<BankAccountStripeOnboardingDialogComponent>,
        private _stripeConnectService: StripeConnectService,
        private _growler: GrowlerService,
    ) {}

    ngAfterViewInit(): void {
        this.initializeStripeConnect();
    }

    close(): void {
        this._dialogRef.close(<BankAccountStripeOnboardingDialogResult>{ exited: true });
    }

    ngOnDestroy(): void {
        if (this._stripeConnectInstance) {
            this._stripeConnectInstance.logout();
            this._stripeConnectInstance = null;
        }
    }

    private initializeStripeConnect(): void {
        this._stripeConnectService.createConnectAccount({
            organizationId: this.data.organizationId,
        }).pipe(
            switchMap((response) =>
                defer(async () => loadConnectAndInitialize({
                    publishableKey: response.publishableKey,
                    fetchClientSecret: async () => response.clientSecret,
                    appearance: STRIPE_APPEARANCE_CONFIG,
                })),
            ),
            tap((stripeConnect) => {
                this._stripeConnectInstance = stripeConnect;
                this.mountOnboardingComponent(stripeConnect);
            }),
            catchError((error) => {
                this._growler.fromErrorResponse(error);
                this._dialogRef.close(<BankAccountStripeOnboardingDialogResult>{ exited: true, error: true });
                return of(null);
            }),
            finalize(() => this.loading$.next(false)),
            untilDestroyed(this),
        ).subscribe();
    }

    private mountOnboardingComponent(stripeConnect: StripeConnectInstance): void {
        const onboardingComponent = stripeConnect.create('account-onboarding');

        onboardingComponent.setFullTermsOfServiceUrl(PAYHOA_URLS.TERMS_OF_SERVICE);
        onboardingComponent.setPrivacyPolicyUrl(PAYHOA_URLS.PRIVACY_POLICY);

        onboardingComponent.setCollectionOptions({
            fields: 'eventually_due',
            futureRequirements: 'include',
        });

        onboardingComponent.setOnStepChange((_stepChange) => {
            // Step change tracking — integrate with analytics when ready
        });

        onboardingComponent.setOnExit(() => {
            this._dialogRef.close(<BankAccountStripeOnboardingDialogResult>{ exited: true });
        });

        if (this.stripeContainer?.nativeElement) {
            this.stripeContainer.nativeElement.appendChild(onboardingComponent);
        }
    }
}
